import { useCallback, useEffect, useState } from "react";

import { TopBar } from "../components/ui/TopBar";
import { Button } from "../components/ui/Button";

function readInset(side: "top" | "right" | "bottom" | "left"): string {
  const probe = document.createElement("div");
  probe.style.position = "fixed";
  probe.style.visibility = "hidden";
  probe.style.paddingTop = `env(safe-area-inset-${side})`;
  document.body.appendChild(probe);
  const v = getComputedStyle(probe).paddingTop;
  document.body.removeChild(probe);
  return v || "0px";
}

function describe(el: Element | null): Record<string, unknown> {
  if (!el) return { present: false };
  const cs = getComputedStyle(el);
  const r = el.getBoundingClientRect();
  return {
    present: true,
    position: cs.position,
    display: cs.display,
    top: cs.top,
    height: cs.height,
    minHeight: cs.minHeight,
    paddingTop: cs.paddingTop,
    paddingBottom: cs.paddingBottom,
    zIndex: cs.zIndex,
    overflowX: cs.overflowX,
    overflowY: cs.overflowY,
    childElementCount: el.childElementCount,
    rect: {
      top: Math.round(r.top),
      bottom: Math.round(r.bottom),
      left: Math.round(r.left),
      height: Math.round(r.height),
      width: Math.round(r.width),
    },
  };
}

function snapshot() {
  const modes = ["fullscreen", "standalone", "minimal-ui", "browser"] as const;
  const displayMode = modes.find((m) => matchMedia(`(display-mode: ${m})`).matches) ?? "unknown";
  const app = document.querySelector(".tcg-app");
  const host = document.querySelector(".tcg-topbar-host");
  const topbar = document.querySelector(".tcg-topbar");
  const scroll = document.querySelector(".tcg-scroll");
  const nav = document.querySelector(".tcg-nav");

  return {
    when: new Date().toISOString(),
    ua: navigator.userAgent,
    displayMode,
    iosNavigatorStandalone: (navigator as unknown as { standalone?: boolean }).standalone ?? null,
    heights: {
      windowInnerHeight: window.innerHeight,
      docClientHeight: document.documentElement.clientHeight,
      screenHeight: window.screen.height,
      visualViewportHeight: window.visualViewport ? Math.round(window.visualViewport.height) : null,
      bodyScrollHeight: document.body.scrollHeight,
    },
    safeAreaInsets: {
      top: readInset("top"),
      right: readInset("right"),
      bottom: readInset("bottom"),
      left: readInset("left"),
    },
    "tcg-app": describe(app),
    "tcg-topbar-host": describe(host),
    "tcg-topbar (portaled bar)": describe(topbar),
    topbarPortalMounted: !!host && host.childElementCount > 0,
    "tcg-scroll": describe(scroll),
    "tcg-nav": describe(nav),
    navGapBelow: nav
      ? Math.round(window.innerHeight - nav.getBoundingClientRect().bottom)
      : null,
  };
}

export function Debug() {
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(() => {
    setText(JSON.stringify(snapshot(), null, 2));
    setCopied(false);
  }, []);

  useEffect(() => {
    // let the portal + layout settle for a frame before measuring
    const id = requestAnimationFrame(refresh);
    return () => cancelAnimationFrame(id);
  }, [refresh]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <>
      <TopBar back title="Debug layout" sub="Diagnostica PWA · nessun dato sensibile" />
      <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" onClick={refresh}>
            Ricalcola
          </Button>
          <Button variant="yellow" style={{ flex: 1 }} onClick={copy}>
            {copied ? "Copiato ✓" : "Copia tutto"}
          </Button>
        </div>
        <textarea
          className="tcg-textarea tcg-mono"
          readOnly
          value={text}
          onFocus={(e) => e.currentTarget.select()}
          style={{ minHeight: 420, fontSize: 11, lineHeight: 1.4, whiteSpace: "pre" }}
        />
      </div>
      <div style={{ height: 24 }} />
    </>
  );
}
